import { useEffect, useMemo, useState } from "react";
import { FaBars } from 'react-icons/fa';
import MobileDrawer from '../components/MobileDrawer';
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import Product from "../components/Product";
import { useGetProductsQuery } from "../slices/productsApiSlice.js";
import Loader from "../components/Loader";
import Message from "../components/Message";
import Paginate from "../components/Paginate";
import SearchHelper from "../components/SearchHelper";
import { useGetExchangeRatesQuery } from "../slices/exchangeSlice.js";
import { formatPrice } from "../utils/formatPrice.js";

function SearchScreen() {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { pageNumber, keyword, sort, min, max } = useParams();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const itemType = searchParams.get("itemType") || "all";
  const language = searchParams.get("language") || "all";

  const queryParams = useMemo(() => {
    const params = {
      keyword: keyword || "",
      pageNumber: pageNumber || 1,
      sort: sort || "",
    };

    if (min) params.min = min;
    if (max) params.max = max;
    if (itemType && itemType !== "all") params.itemType = itemType;
    if (language && language !== "all") params.language = language;

    return params;
  }, [keyword, pageNumber, sort, min, max, itemType, language]);

  const { data, isLoading, error } = useGetProductsQuery(queryParams);
  const { data: exchangeData } = useGetExchangeRatesQuery();

  const userInfo = useSelector((state) => state.auth.userInfo);
  const preferredCurrency = userInfo?.preferredCurrency || "USD";

  const processedProducts = useMemo(() => {
    if (!data?.products?.length) return [];

    return data.products.map((product) => {
      let displayPrice = product.price;
      if (preferredCurrency !== "USD") {
        displayPrice = formatPrice(product.price, preferredCurrency, exchangeData);
      } else {
        displayPrice = product.price.toFixed(2);
      }
      return { ...product, displayPrice };
    });
  }, [data?.products, preferredCurrency, exchangeData]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pageNumber, keyword, sort, min, max, itemType, language]);

  if (isLoading) return <Loader />;
  if (error) {
    return <Message variant="danger">{error?.data?.message || error.error}</Message>;
  }

  return (
    <div className="space-y-6 pb-8">
      <section className="flex flex-wrap items-center gap-4 text-sm text-slate-700">
        <p className="m-0 text-base">
          <span className="font-semibold text-slate-900">Keyword:</span>{" "}
          {keyword ? `"${keyword}"` : "All"}
        </p>
        <p className="m-0 text-base">
          <span className="font-semibold text-slate-900">{data?.count ?? 0}</span>{" "}
          result{(data?.count ?? 0) === 1 ? "" : "s"}
        </p>
      </section>

      <button type="button" onClick={() => setFiltersOpen(true)} aria-expanded={filtersOpen} aria-haspopup="dialog"
        className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-sky-200 bg-white px-4 py-2 text-sm font-semibold text-sky-800 shadow-sm transition hover:bg-sky-50 active:scale-[0.98] lg:hidden">
        <FaBars /> Filters
      </button>
      {filtersOpen && <MobileDrawer title="Filters" variant="filters" onClose={() => setFiltersOpen(false)}>
        <div className="category-intro">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-sky-700">Practidex library</span>
          <p className="mb-1 mt-2 text-xl font-semibold text-slate-900">Find the right fit</p>
          <p className="m-0 text-sm leading-6 text-slate-600">Refine your results by price, resource type, and language.</p>
        </div>
        <SearchHelper drawer />
        <button type="button" onClick={() => setFiltersOpen(false)}
          className="sticky bottom-0 mt-5 min-h-[48px] w-full rounded-xl bg-sky-600 px-4 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-sky-700 active:scale-[0.98]">Show results</button>
      </MobileDrawer>}
      <section className="hidden lg:block">
        <SearchHelper />
      </section>

      {processedProducts.length > 0 ? (
        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {processedProducts.map((product) => (
            <Product
              key={product._id}
              product={product}
              displayPrice={product.displayPrice}
              currency={preferredCurrency}
                  className=""
                />
          ))}
        </section>
      ) : (
        <section className="rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm">
          <h2 className="m-0 text-xl font-semibold text-slate-800">No items were found</h2>
          <p className="mt-2 text-sm text-slate-600">
            Try changing search terms, sort order, or price range.
          </p>
        </section>
      )}

      <Paginate
        pages={data.pages}
        page={data.page}
        keyword={keyword ? keyword : ""}
        sort={sort ? sort : ""}
        min={min ? min : ""}
        max={max ? max : ""}
        itemType={itemType}
        language={language}
      />
    </div>
  );
}

export default SearchScreen;
